import { Module } from '../types';

export const STYLING_MODULE: Module = {
  id: "advanced-2",
  title: "Modern CSS-in-JS & Styling Solutions",
  description: "Master modern styling approaches including CSS-in-JS, design systems, and advanced CSS techniques.",
  duration: "1.5 hours",
  estimatedTime: 85,
  difficulty: "intermediate",
  prerequisites: ["advanced-1"],
  content: `
# Modern CSS-in-JS & Styling Solutions

## Learning Objectives
- Compare and implement various CSS-in-JS solutions
- Build scalable design systems with styled-components
- Master CSS modules and PostCSS workflows
- Implement theming and dynamic styling
- Optimize styling performance
- Create responsive and accessible designs

## CSS-in-JS Libraries Comparison

### Styled-Components
\`\`\`jsx
import styled, { css, ThemeProvider } from 'styled-components';

// Basic styled component
const Button = styled.button\`
  background: \${props => props.primary ? '#007bff' : '#6c757d'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  \${props => props.size === 'large' && css\`
    padding: 1rem 2rem;
    font-size: 1.125rem;
  \`}

  \${props => props.size === 'small' && css\`
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  \`}
\`;

// Advanced component with complex logic
const Card = styled.div\`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition: all 0.3s ease;

  \${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return css\`
          border-left: 4px solid \${theme.colors.success};
          background: \${theme.colors.successLight};
        \`;
      case 'warning':
        return css\`
          border-left: 4px solid \${theme.colors.warning};
          background: \${theme.colors.warningLight};
        \`;
      case 'error':
        return css\`
          border-left: 4px solid \${theme.colors.error};
          background: \${theme.colors.errorLight};
        \`;
      default:
        return css\`
          border: 1px solid \${theme.colors.border};
        \`;
    }
  }}

  @media (max-width: \${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
    margin: 0.5rem;
  }
\`;

// Usage with theme
const theme = {
  colors: {
    primary: '#007bff',
    success: '#28a745',
    warning: '#ffc107',
    error: '#dc3545',
    successLight: '#d4edda',
    warningLight: '#fff3cd',
    errorLight: '#f8d7da',
    border: '#dee2e6'
  },
  breakpoints: {
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px'
  }
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <div>
        <Button primary size="large">Primary Button</Button>
        <Card variant="success">
          <h3>Success Card</h3>
          <p>This is a success message.</p>
        </Card>
      </div>
    </ThemeProvider>
  );
}
\`\`\`

### Emotion
\`\`\`jsx
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';

// CSS prop approach
const buttonStyles = (props) => css\`
  background: \${props.primary ? '#007bff' : '#6c757d'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
\`;

function EmotionButton({ primary, children, ...props }) {
  return (
    <button css={buttonStyles({ primary })} {...props}>
      {children}
    </button>
  );
}

// Styled approach (similar to styled-components)
const EmotionCard = styled.div\`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  \${props => props.highlighted && css\`
    border: 2px solid #007bff;
    box-shadow: 0 4px 16px rgba(0, 123, 255, 0.2);
  \`}
\`;
\`\`\`

## Design System Implementation

### Component Library Structure
\`\`\`jsx
// Design tokens
export const tokens = {
  colors: {
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      500: '#3b82f6',
      600: '#2563eb',
      900: '#1e3a8a'
    },
    gray: {
      50: '#f9fafb',
      100: '#f3f4f6',
      500: '#6b7280',
      900: '#111827'
    }
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem'
  },
  typography: {
    fontSizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem'
    },
    fontWeights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    }
  },
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px'
  }
};

// Base component system
const BaseButton = styled.button\`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: \${tokens.borderRadius.md};
  font-weight: \${tokens.typography.fontWeights.medium};
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
\`;

// Variant system
const buttonVariants = {
  primary: css\`
    background: \${tokens.colors.primary[600]};
    color: white;
    
    &:hover:not(:disabled) {
      background: \${tokens.colors.primary[700]};
    }
  \`,
  secondary: css\`
    background: \${tokens.colors.gray[100]};
    color: \${tokens.colors.gray[900]};
    
    &:hover:not(:disabled) {
      background: \${tokens.colors.gray[200]};
    }
  \`,
  outline: css\`
    background: transparent;
    color: \${tokens.colors.primary[600]};
    border: 1px solid \${tokens.colors.primary[600]};
    
    &:hover:not(:disabled) {
      background: \${tokens.colors.primary[50]};
    }
  \`
};

const buttonSizes = {
  sm: css\`
    padding: \${tokens.spacing.xs} \${tokens.spacing.sm};
    font-size: \${tokens.typography.fontSizes.sm};
  \`,
  md: css\`
    padding: \${tokens.spacing.sm} \${tokens.spacing.md};
    font-size: \${tokens.typography.fontSizes.base};
  \`,
  lg: css\`
    padding: \${tokens.spacing.md} \${tokens.spacing.lg};
    font-size: \${tokens.typography.fontSizes.lg};
  \`
};

// Final Button component
export const Button = styled(BaseButton)\`
  \${props => buttonVariants[props.variant || 'primary']}
  \${props => buttonSizes[props.size || 'md']}
  
  \${props => props.fullWidth && css\`
    width: 100%;
  \`}
\`;
\`\`\`

## CSS Modules with TypeScript

### Advanced CSS Modules Setup
\`\`\`css
/* Button.module.css */
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary {
  background: #3b82f6;
  color: white;
}

.primary:hover:not(:disabled) {
  background: #2563eb;
}

.secondary {
  background: #f3f4f6;
  color: #374151;
}

.secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.small {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.medium {
  padding: 0.5rem 1rem;
  font-size: 1rem;
}

.large {
  padding: 0.75rem 1.5rem;
  font-size: 1.125rem;
}

.fullWidth {
  width: 100%;
}

/* Responsive utilities */
@media (max-width: 768px) {
  .responsiveStack {
    display: block;
    width: 100%;
  }
  
  .responsiveStack + .responsiveStack {
    margin-top: 0.5rem;
  }
}
\`\`\`

\`\`\`tsx
// Button.tsx
import React from 'react';
import styles from './Button.module.css';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  responsive?: boolean;
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  responsive = false,
  className,
  children,
  ...props
}) => {
  const buttonClasses = clsx(
    styles.button,
    styles[variant],
    styles[size],
    {
      [styles.fullWidth]: fullWidth,
      [styles.responsiveStack]: responsive
    },
    className
  );

  return (
    <button className={buttonClasses} {...props}>
      {children}
    </button>
  );
};
\`\`\`

## Theming and Dynamic Styles

### Advanced Theming System
\`\`\`jsx
import { createContext, useContext, useState } from 'react';

// Theme context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme definitions
const themes = {
  light: {
    colors: {
      background: '#ffffff',
      surface: '#f8f9fa',
      primary: '#007bff',
      secondary: '#6c757d',
      text: '#212529',
      textMuted: '#6c757d',
      border: '#dee2e6'
    },
    shadows: {
      sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
      md: '0 4px 6px rgba(0, 0, 0, 0.1)',
      lg: '0 10px 15px rgba(0, 0, 0, 0.1)'
    }
  },
  dark: {
    colors: {
      background: '#121212',
      surface: '#1e1e1e',
      primary: '#1976d2',
      secondary: '#757575',
      text: '#ffffff',
      textMuted: '#b0b0b0',
      border: '#333333'
    },
    shadows: {
      sm: '0 1px 3px rgba(0, 0, 0, 0.3)',
      md: '0 4px 6px rgba(0, 0, 0, 0.3)',
      lg: '0 10px 15px rgba(0, 0, 0, 0.3)'
    }
  }
};

// Theme provider
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('light');
  
  const theme = themes[currentTheme];
  
  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    currentTheme,
    toggleTheme,
    setTheme: setCurrentTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      <div style={{
        backgroundColor: theme.colors.background,
        color: theme.colors.text,
        minHeight: '100vh',
        transition: 'background-color 0.3s ease'
      }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

// Themed components
const ThemedCard = styled.div\`
  background: \${({ theme }) => theme.colors.surface};
  border: 1px solid \${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: \${({ theme }) => theme.shadows.md};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: \${({ theme }) => theme.shadows.lg};
  }
\`;

// Usage
function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

function ThemedApp() {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <div>
      <button onClick={toggleTheme}>
        Switch to {theme === themes.light ? 'Dark' : 'Light'} Theme
      </button>
      <ThemedCard>
        <h2>Themed Card</h2>
        <p>This card adapts to the current theme.</p>
      </ThemedCard>
    </div>
  );
}
\`\`\`

## Performance Optimization

### Optimizing CSS-in-JS Performance
\`\`\`jsx
import { memo } from 'react';
import styled from 'styled-components';

// Avoid creating styled components inside render
// ❌ Bad - creates new component on every render
function BadComponent({ color }) {
  const DynamicDiv = styled.div\`
    color: \${color};
  \`;
  
  return <DynamicDiv>Content</DynamicDiv>;
}

// ✅ Good - component created outside render
const StyledDiv = styled.div\`
  color: \${props => props.color};
\`;

function GoodComponent({ color }) {
  return <StyledDiv color={color}>Content</StyledDiv>;
}

// Memoize expensive styled components
const ExpensiveCard = memo(styled.div\`
  background: linear-gradient(
    45deg,
    \${props => props.theme.colors.primary},
    \${props => props.theme.colors.secondary}
  );
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  // Expensive animations and calculations
  animation: float 3s ease-in-out infinite;
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
\`);

// Use shouldForwardProp to prevent unnecessary DOM attributes
const OptimizedButton = styled.button.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) => {
    // Don't forward custom props to DOM
    if (prop === 'variant' || prop === 'size') {
      return false;
    }
    return defaultValidatorFn(prop);
  }
})\`
  background: \${props => props.variant === 'primary' ? '#007bff' : '#6c757d'};
  padding: \${props => props.size === 'large' ? '1rem 2rem' : '0.5rem 1rem'};
\`;
\`\`\`

## Responsive Design Patterns

### Advanced Responsive Utilities
\`\`\`jsx
// Responsive breakpoint system
const breakpoints = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1400px'
};

// Media query helpers
const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css\`
    @media (min-width: \${breakpoints[label]}) {
      \${css(...args)}
    }
  \`;
  return acc;
}, {});

// Container component with responsive padding
const Container = styled.div\`
  width: 100%;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-left: auto;
  margin-right: auto;

  \${media.sm\`
    max-width: 540px;
  \`}

  \${media.md\`
    max-width: 720px;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  \`}

  \${media.lg\`
    max-width: 960px;
  \`}

  \${media.xl\`
    max-width: 1140px;
  \`}

  \${media.xxl\`
    max-width: 1320px;
  \`}
\`;

// Responsive grid system
const Grid = styled.div\`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  \${media.sm\`
    grid-template-columns: repeat(2, 1fr);
  \`}

  \${media.md\`
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  \`}

  \${media.lg\`
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
  \`}
\`;

// Responsive typography
const ResponsiveHeading = styled.h1\`
  font-size: 1.5rem;
  line-height: 1.2;
  margin-bottom: 1rem;

  \${media.sm\`
    font-size: 2rem;
  \`}

  \${media.md\`
    font-size: 2.5rem;
  \`}

  \${media.lg\`
    font-size: 3rem;
  \`}
\`;
\`\`\`
`,
  videoUrl: "https://example.com/styling-solutions",
  codeExamples: [
    {
      title: "Styled-Components Theme System",
      code: `import styled, { ThemeProvider } from 'styled-components';

const theme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d'
  },
  breakpoints: {
    md: '768px'
  }
};

const Button = styled.button\`
  background: \${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  
  @media (max-width: \${props => props.theme.breakpoints.md}) {
    padding: 0.5rem 1rem;
  }
\`;

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Button>Themed Button</Button>
    </ThemeProvider>
  );
}`,
      explanation: "Complete theming system with styled-components and responsive design",
      language: "jsx"
    }
  ],
  practice: {
    task: "Build a complete design system with dark/light theme support, responsive components, and a component library. Include buttons, cards, forms, and navigation components.",
    starter: `import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { createContext, useContext, useState } from 'react';

// Define your themes
const lightTheme = {
  // Add light theme colors and styles
};

const darkTheme = {
  // Add dark theme colors and styles
};

// Create theme context
const ThemeContext = createContext();

// Create components
const Button = styled.button\`
  // Add button styles
\`;

const Card = styled.div\`
  // Add card styles
\`;

function DesignSystem() {
  // Implement theme switching and component showcase
}`,
    solution: `import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { createContext, useContext, useState } from 'react';

const lightTheme = {
  colors: {
    primary: '#007bff',
    secondary: '#6c757d',
    background: '#ffffff',
    surface: '#f8f9fa',
    text: '#212529',
    textMuted: '#6c757d',
    border: '#dee2e6'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)'
  }
};

const darkTheme = {
  colors: {
    primary: '#0d6efd',
    secondary: '#6c757d',
    background: '#121212',
    surface: '#1e1e1e',
    text: '#ffffff',
    textMuted: '#adb5bd',
    border: '#495057'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px rgba(0, 0, 0, 0.3)'
  }
};

const GlobalStyle = createGlobalStyle\`
  body {
    background: \${props => props.theme.colors.background};
    color: \${props => props.theme.colors.text};
    transition: background-color 0.3s ease, color 0.3s ease;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
\`;

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const Button = styled.button\`
  background: \${props => props.variant === 'secondary' 
    ? props.theme.colors.secondary 
    : props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
\`;

const Card = styled.div\`
  background: \${props => props.theme.colors.surface};
  border: 1px solid \${props => props.theme.colors.border};
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: \${props => props.theme.shadows.md};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: \${props => props.theme.shadows.lg};
  }
\`;

const Grid = styled.div\`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
\`;

function DesignSystem() {
  const [isDark, setIsDark] = useState(false);
  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <ThemeContext.Provider value={{ isDark, setIsDark }}>
        <div style={{ padding: '2rem' }}>
          <h1>Design System</h1>
          <Button onClick={() => setIsDark(!isDark)}>
            Switch to {isDark ? 'Light' : 'Dark'} Theme
          </Button>
          
          <Grid>
            <Card>
              <h3>Component Library</h3>
              <p>Explore our design system components.</p>
              <Button>Primary Action</Button>
              <Button variant="secondary">Secondary Action</Button>
            </Card>
            
            <Card>
              <h3>Responsive Design</h3>
              <p>All components adapt to different screen sizes.</p>
            </Card>
          </Grid>
        </div>
      </ThemeContext.Provider>
    </ThemeProvider>
  );
}`,
    hints: [
      "Create comprehensive theme objects with colors, shadows, and spacing",
      "Use createGlobalStyle for body styling",
      "Implement theme context for state management",
      "Add hover effects and transitions for better UX"
    ]
  },
  quickCheck: {
    questions: [
      {
        id: "styling-q1",
        question: "What is the main advantage of CSS-in-JS over traditional CSS?",
        options: [
          "Better performance",
          "Dynamic styling and component-scoped styles",
          "Smaller bundle sizes",
          "Better browser support"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "easy"
      },
      {
        id: "styling-q2",
        question: "Which approach helps prevent CSS naming conflicts?",
        options: [
          "Global styles",
          "Inline styles only",
          "CSS Modules and CSS-in-JS",
          "Using !important"
        ],
        correctAnswer: 2,
        correctAnswerIndex: 2,
        difficulty: "medium"
      },
      {
        id: "styling-q3",
        question: "What should you avoid when using styled-components for performance?",
        options: [
          "Using props in styles",
          "Creating styled components inside render functions",
          "Using ThemeProvider",
          "Using media queries"
        ],
        correctAnswer: 1,
        correctAnswerIndex: 1,
        difficulty: "hard"
      }
    ]
  }
};
